import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { useAllocatedDedicatedCli } from '../api';
import type { CliRow } from '../types';

/**
 * USER role — read-only list of the dedicated CLIs allocated to this user.
 * No buttons / actions: allocation and removal are done by the reseller.
 * Source: GET /api/obd/dedicated/cli/allocated/:userId (USER role allowed).
 */
export function UserDedicatedCliListPage() {
  const userId = useAuthStore((s) => s.userId);
  const query = useAllocatedDedicatedCli(userId);

  // The API answers "no data" (error) when nothing is allocated → show empty table.
  const rows = query.data ?? [];

  const columns = useMemo<ColumnDef<CliRow>[]>(
    () => [
      {
        accessorKey: 'cliId',
        header: 'CLI ID',
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.cliId}</span>
        ),
      },
      { accessorKey: 'cli', header: 'CLI' },
      { accessorKey: 'allocationDate', header: 'Allocated', size: 180 },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="All CLIs"
        description="Dedicated caller-line identities allocated to your account."
      />

      <DataTable<CliRow, unknown>
        tableId="cli:user-dedicated:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search CLIs…"
        emptyTitle="No dedicated CLIs"
        emptyDescription="No dedicated CLI has been allocated to your account yet."
        getRowId={(row) => String(row.cliId)}
        exportFilename="my-dedicated-cli.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
